import { get, post, put, del } from '@utilities';
import { type AxiosResponse } from 'axios';

export const getProjects = async (data: Record<string, any>): Promise<AxiosResponse> => await post('project/all', data);
export const addProject = async (data: Record<string, any>): Promise<AxiosResponse> => await post('project', data);
export const getProjectDetails = async (projectID: string): Promise<AxiosResponse> => await post(`project/${projectID}`);
export const updateProjectDetails = async (data: any, projectID): Promise<AxiosResponse> =>
	await put(`project/${projectID}`, data);
export const deleteProject = async (projectID: string): Promise<AxiosResponse> => await del(`project/${projectID}`);
export const getProjectActivities = async (projectID: string): Promise<AxiosResponse> =>
	await get(`scheduler/${projectID}/project`);
export const completeProjectActivity = async (data: any, schedulerID: any): Promise<AxiosResponse> =>
	await put(`scheduler/${schedulerID}`, data);
export const getApprovals = async (data: Record<string, any>): Promise<AxiosResponse> => await post('approval/all', data);
export const approveRequest = async (approvalId: any): Promise<AxiosResponse> => await put(`approval/${approvalId}`);
export const rejectRequest = async (approvalId: any): Promise<AxiosResponse> => await put(`approval/${approvalId}/reject`);
export const updateActivityDetails = async (data: any, schedulerID: any): Promise<AxiosResponse> =>
	await put(`scheduler/${schedulerID}/days`, data);
export const scheduleSiteVisit = async (schedulerID: any): Promise<AxiosResponse> =>
	await put(`scheduler/${schedulerID}/site-visit`);
export const uploadActivityDocuments = async (data, schedulerID: any): Promise<AxiosResponse> =>
	await put(`scheduler/${schedulerID}/docs`, data);
export const activateDocument = async (data: Record<string, any>): Promise<AxiosResponse> => await post(`document/activate`, data);
export const fetchActivityDocuments = async (docIds: any): Promise<AxiosResponse> => await post(`document/all`, docIds);
export const deleteActivityDocuments = async (docIds: any): Promise<AxiosResponse> =>
	await put(`document/document_url/delete`, docIds);
export const getLedgerData = async (projectID: string): Promise<AxiosResponse> => await get(`ledger/${projectID}`);

export const recalculateLedger = async (projectID: string | number): Promise<AxiosResponse> =>
	await post(`ledger/${projectID}/recalculate`, {});

export const previewLedgerTokenAdjustment = async (
	projectID: string | number, data: { tokenAmount: number; reason: string }
): Promise<AxiosResponse> => await post(`ledger/${projectID}/token-adjustment/preview`, data);

export const applyLedgerTokenAdjustment = async (
	projectID: string | number, data: { tokenAmount: number; reason: string; expectedState: string }
): Promise<AxiosResponse> => await post(`ledger/${projectID}/token-adjustment`, data);

export const getLedgerTokenAdjustments = async (projectID: string | number): Promise<AxiosResponse> =>
	await get(`ledger/${projectID}/token-adjustments`);

export type LedgerTranche = 'TOKEN' | 'PRE_PRODUCTION' | 'PRE_INSTALLATION';
export type LedgerTrancheAdjustment = { tranche: LedgerTranche; amount: number; remainderTranches: LedgerTranche[]; reason: string };
export const previewLedgerTrancheAdjustment = async (projectID: string | number, data: LedgerTrancheAdjustment): Promise<AxiosResponse> =>
	await post(`ledger/${projectID}/tranche-adjustment/preview`, data);
export const applyLedgerTrancheAdjustment = async (projectID: string | number, data: LedgerTrancheAdjustment & { expectedState: string }): Promise<AxiosResponse> =>
	await post(`ledger/${projectID}/tranche-adjustment`, data);
export const getLedgerTrancheAdjustments = async (projectID: string | number): Promise<AxiosResponse> =>
	await get(`ledger/${projectID}/tranche-adjustments`);
export const getOrders = async (data: Record<string, any>): Promise<AxiosResponse> => await post('project/order/all', data);
export const dropdownUser = async (department?: string | null): Promise<AxiosResponse> => {
	if (department) {
		return get(`user/autocomplete?user_type=INTERNAL&department=${department}`);
	}
	return get('user/autocomplete?user_type=INTERNAL');
};
export const addProjectDiscount = async (data, projectID: any): Promise<AxiosResponse> =>
	await post(`ledger/${projectID}/discount`, data);
export const fetchProjectAssignees = async (projectID: string): Promise<AxiosResponse> =>
	await get(`project/${projectID}/assignees`);
export const getProjectDiscounts = async (projectID: string): Promise<AxiosResponse> =>
	await get(`ledger/${projectID}/discounts`);
export const applyPromoCode = async (data: { projectId: number; promoCode: string }): Promise<AxiosResponse> =>
	await post(`promo-code/apply`, data);
export const removePromoCode = async (data: { projectId: number; promoCode: string }): Promise<AxiosResponse> =>
	await post(`promo-code/remove`, data);
export const getWarrantyClaims = async (data: Record<string, any>): Promise<AxiosResponse> => await post('warranty-claim/all', data);
export const getTechnicianWarrantyClaims = async (data: Record<string, any>): Promise<AxiosResponse> =>
	await post('warranty-claim/technician/all', data);
export const getWarrantyClaimById = async (claimId: string): Promise<AxiosResponse> =>
	await get(`warranty-claim/${claimId}`);
export const updateWarrantyClaim = async (claimId: string, data: any): Promise<AxiosResponse> =>
	await put(`warranty-claim/${claimId}`, data);
export const decideWarrantyClaim = async (
	claimId: string,
	action: 'APPROVE' | 'REJECT'
): Promise<AxiosResponse> => await put(`warranty-claim/${claimId}/decision`, { action });
export const approveWarrantyClaim = async (claimId: string): Promise<AxiosResponse> =>
	await decideWarrantyClaim(claimId, 'APPROVE');
export const rejectWarrantyClaim = async (claimId: string): Promise<AxiosResponse> =>
	await decideWarrantyClaim(claimId, 'REJECT');
export const scheduleWarrantyClaim = async (
	claimId: string,
	data: {
		warrantyScopes: Array<{
			warrantyCode: string;
			siteVisitTime: number;
			assignedTechnician: number;
		}>;
	}
): Promise<AxiosResponse> => await put(`warranty-claim/${claimId}/schedule`, data);
export const updateWarrantyClaimScheduleDetails = async (
	claimId: string,
	data: {
		warrantyScopes: Array<{
			warrantyCode: string;
			siteVisitTime: number;
			assignedTechnician: number;
		}>;
	}
): Promise<AxiosResponse> => await put(`warranty-claim/${claimId}/schedule-details`, data);
export const submitWarrantyQuote = async (
	claimId: string,
	data: {
		warrantyCode: string;
		charges: {
			serviceCharge?: number | null;
			siteVisitCharge?: number | null;
			productCharges?: Array<{
				material?: { code?: string; name?: string; price?: number };
				qty?: number;
				price?: number;
			}>;
			additionalCharges?: Array<{
				material?: { code?: string; name?: string; price?: number };
				qty?: number;
				price?: number;
			}>;
		};
		attachments: Array<{ file: string; coverImage?: boolean }>;
	}
): Promise<AxiosResponse> => await put(`warranty-claim/${claimId}/quote`, data);
export const submitWarrantyFinalQuote = async (
	claimId: string,
	data: {
		warrantyCode: string;
		charges: {
			serviceCharge?: number | null;
			siteVisitCharge?: number | null;
			productCharges?: Array<{
				material?: { code?: string; name?: string; price?: number };
				qty?: number;
				price?: number;
			}>;
			additionalCharges?: Array<{
				material?: { code?: string; name?: string; price?: number };
				qty?: number;
				price?: number;
			}>;
		};
		attachments: Array<{ file: string; coverImage?: boolean }>;
	}
): Promise<AxiosResponse> => await put(`warranty-claim/${claimId}/final-quote`, data);
export const updateWarrantyCharges = async (
	claimId: string,
	data: {
		warrantyCode: string;
		charges: {
			serviceCharge?: number | null;
			siteVisitCharge?: number | null;
			productCharges?: Array<{
				material?: { code?: string; name?: string; price?: number };
				qty?: number;
				price?: number;
			}>;
			additionalCharges?: Array<{
				material?: { code?: string; name?: string; price?: number };
				qty?: number;
				price?: number;
			}>;
		};
	}
): Promise<AxiosResponse> => await put(`warranty-claim/${claimId}/charges`, data);
export const updateWarrantyClaimStatuses = async (
	claimId: string,
	data: {
		status?: string;
		warrantyScopes?: Array<{
			warrantyCode: string;
			warrantyStatus: string;
		}>;
	}
): Promise<AxiosResponse> => await put(`warranty-claim/${claimId}/status`, data);
export const updateWarrantyClaimDiscount = async (
	claimId: string,
	data: {
		discountAmount: number;
		discountReason?: string;
	}
): Promise<AxiosResponse> => await put(`warranty-claim/${claimId}/discount`, data);
export const fetchWarrantyProductMaterials = async (): Promise<AxiosResponse> =>
	await get('costing/builder-raw-materials');
export const fetchWarrantyDocuments = async (docIds: any): Promise<AxiosResponse> => await post(`document/all`, docIds);
export const getProjectWarranty = async (projectId: number): Promise<AxiosResponse> =>
	await get(`project/${projectId}/warranty`);
export const putLifetimeWarranty = async (projectId: number): Promise<AxiosResponse> =>
	await put(`project/${projectId}/warranty/lifetime`, {});
export const editDiscountAmount = async (projectId: number, amount: number): Promise<AxiosResponse> =>
	await put(`ledger/${projectId}/discount/amt?amt=${amount}`, {});

export const editCashDiscountAmount = async (projectId: number, amount: number): Promise<AxiosResponse> =>
	await put(`ledger/${projectId}/discount/amount?amt=${amount}`, {});
export const getDescopeApprovals = async (): Promise<AxiosResponse> => await get('approval/descope/all');
export const approveDescopeRequest = async (approvalId: number): Promise<AxiosResponse> =>
	await put(`approval/descope/${approvalId}`, {});
export const getOptimizedCutlistData = async (projectId: string | number): Promise<AxiosResponse> => {
	if (!projectId || String(projectId).toLowerCase() === 'undefined' || String(projectId).toLowerCase() === 'null') {
		throw new Error('Project id is required to fetch optimized cutlist');
	}
	return await get(`quotation/cutlist/${projectId}/optimized`);
};
